import React, { useMemo, useState } from 'react';
import { crossReading, palaceReading } from '../qimen/analysis.js';
import { GONG_BY_NUM, MEN_BY_NAME, STAR_BY_NAME } from '../qimen/constants.js';

/* ============================================================
 * 奇门 · 盘面关系判读（提问后才出现的可展开面板）
 * 盘面本身保持原样不动：所有关系、生克、符号标注都画在这里。
 *   ① 关系图：用神落宫 → 求测人宫 → 事体宫，生克以箭头与颜色标出
 *   ② 主轴对照 / 单宫读象（求测人宫、事体宫、所选宫）
 *   ③ 用神定位、关键宫、十干克应、四害、应期线索、断语
 * 断法依阴盘奇门：以象意为先、以宫位为体（日干为身、时干为事）。
 * ============================================================ */

const TONE = { 吉: '#1E8B4C', 凶: '#D0342C', 平: '#B0800F' };
const KIND_DASH = { 克: '5 4', 生: undefined, 比: '1 3', 同宫: undefined, '—': '2 4' };

function Pillar({ title, p, roleLabel }) {
  if (!p) return <div className="qm-pillar qm-pillar--empty">该用神未上盘</div>;
  const men = p.men ? MEN_BY_NAME[p.men] : null;
  return (
    <div className="qm-pillar">
      <div className="qm-pillar-head">
        <span className="qm-pillar-role">{roleLabel}</span>
        <b className="qm-pillar-gua">{p.gua}{p.num}</b>
        <span className="qm-pillar-wx">{p.wx}</span>
        <span className="qm-pillar-dir">{title}</span>
      </div>
      <div className="qm-pillar-body">
        <span className="qm-kv"><i>神</i>{p.shen || '—'}</span>
        <span className="qm-kv"><i>门</i>{p.men || '—'}{men ? `（${men.wx}·${men.luck}）` : ''}</span>
        <span className="qm-kv"><i>星</i>{(p.stars || []).join('') || '—'}</span>
        <span className="qm-kv"><i>干</i>{(p.tianpan || []).join('') || '—'}加{p.dipan || '—'}</span>
        {p.menRel && <span className={`qm-rel qm-rel--${p.menRel.tone}`} title={p.menRel.detail}>{p.menRel.key}</span>}
        {(p.starRels || []).map((s) => (
          <span key={s.star} className="qm-kv qm-kv--sub" title={s.rel?.text}>
            {s.star}：{s.wangShuai}{STAR_BY_NAME[s.star] ? `·${STAR_BY_NAME[s.star].luck}` : ''}
          </span>
        ))}
      </div>
      {(p.harms || []).length > 0 && (
        <div className="qm-pillar-harms">
          {p.harms.map((h, i) => <span key={i} className={`qm-harm qm-harm--${h.type}`} title={h.detail}>{h.type}</span>)}
        </div>
      )}
    </div>
  );
}

/* ---------- 关系图：所有生克与用神落宫都画在面板里 ---------- */
function RelationDiagram({ chart, rd }) {
  const nodes = rd.yong.filter((y) => y.palaces && y.palaces.length).slice(0, 5);
  const rowH = 46;
  const height = Math.max(150, nodes.length * rowH + 26);
  const cy = height / 2;
  const selfX = 176;
  const matterX = 300;

  const selfP = rd.selfPalace;
  const matterP = rd.matterPalace;
  const box = (x, y, w, h, label, sub, color, strong) => (
    <g key={x + '-' + y + '-' + label}>
      <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={9}
        fill={strong ? '#17120a' : '#12141d'} stroke={color} strokeWidth={strong ? 1.8 : 1.1} />
      <text x={x} y={y - 1} textAnchor="middle" fontSize={12} fontWeight={700} fill="#2c2620">{label}</text>
      {sub && <text x={x} y={y + 12} textAnchor="middle" fontSize={9.5} fill="#8a7f6a">{sub}</text>}
    </g>
  );

  return (
    <div className="qm-diagram-wrap">
      <svg className="qm-diagram" viewBox={`0 0 340 ${height}`} role="img" aria-label="盘面关系图">
        <defs>
          {['吉', '凶', '平'].map((t) => (
            <marker key={t} id={`qm-d-arrow-${t}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={TONE[t]} />
            </marker>
          ))}
        </defs>

        {selfP && matterP && selfP.num !== matterP.num && (() => {
          const ax = selfP.num < matterP.num ? 1 : -1;
          void ax;
          const from = selfX + 40;
          const to = matterX - 40;
          const t = rd.axis?.tone || '平';
          const midY = cy - 46;
          return (
            <g key="axis">
              <line x1={from} y1={cy - 8} x2={to} y2={cy - 8} stroke={TONE[t]} strokeWidth={2}
                strokeDasharray={KIND_DASH[rd.axis?.kind] || undefined} markerEnd={`url(#qm-d-arrow-${t})`} />
              <text x={(from + to) / 2} y={cy - 14} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={TONE[t]}>
                {rd.axis?.rel}
              </text>
              <text x={(from + to) / 2} y={midY + 4} textAnchor="middle" fontSize={9} fill="#8a7f6a">
                {rd.axis?.actor === 'b' ? '事宫克身宫' : rd.axis?.actor === 'a' ? '身宫克事宫' : '两宫比和'}
              </text>
            </g>
          );
        })()}

        {nodes.map((y, i) => {
          const ny = 22 + i * rowH;
          const p0 = palaceReading(chart, y.palaces[0]);
          const rel = selfP && p0 ? (() => {
            const a = p0.wx; const b = selfP.wx;
            const SH = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };
            const KE = { 木: '土', 土: '水', 水: '火', 火: '金', 金: '木' };
            if (a === b) return { key: '比和', kind: '比', tone: '平' };
            if (SH[a] === b) return { key: `${a}生${b}`, kind: '生', tone: '吉' };
            if (SH[b] === a) return { key: `${b}生${a}`, kind: '生', tone: '吉' };
            if (KE[a] === b) return { key: `${a}克${b}`, kind: '克', tone: '凶' };
            return { key: `${b}克${a}`, kind: '克', tone: '凶' };
          })() : null;
          const color = rel ? TONE[rel.tone] : '#8E8E93';
          return (
            <g key={'y' + i}>
              <line x1={98} y1={ny} x2={selfX - 44} y2={ny} stroke={color} strokeWidth={1.6}
                strokeDasharray={rel ? KIND_DASH[rel.kind] : undefined} markerEnd={`url(#qm-d-arrow-${rel ? rel.tone : '平'})`} />
              {rel && (
                <>
                  <rect x={(98 + selfX - 44) / 2 - 20} y={ny - 24} width={40} height={15} rx={7} fill="#12141d" stroke={color} strokeWidth={0.8} />
                  <text x={(98 + selfX - 44) / 2} y={ny - 13} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={color}>{rel.key}</text>
                </>
              )}
              <rect x={6} y={ny - 16} width={90} height={32} rx={8} fill="#fdfcf9" stroke="#e6e1d6" />
              <text x={50} y={ny - 3} textAnchor="middle" fontSize={11} fontWeight={700} fill="#2c2620">
                {y.role}
              </text>
              <text x={50} y={ny + 10} textAnchor="middle" fontSize={9.5} fill="#8a7f6a">
                {y.symbol}·宫{y.palaces.join('/')}
              </text>
            </g>
          );
        })}

        {selfP && box(selfX, cy, 84, 40, `${selfP.gua}${selfP.num}`, `体·日干`, '#f2eee3', true)}
        {matterP && box(matterX, cy, 84, 40, `${matterP.gua}${matterP.num}`, `用·时干`, '#c9a227', true)}
        <text x={selfX} y={cy + 34} textAnchor="middle" fontSize={9} fill="#8a7f6a">求测人宫</text>
        <text x={matterX} y={cy + 34} textAnchor="middle" fontSize={9} fill="#8a7f6a">事体宫</text>
      </svg>
      <div className="qm-diagram-legend">
        <span>实线＝相生／比和，虚线＝相克；箭头指“作用于谁”</span>
        <span>左侧为本题用神（按所问之事取），右侧两宫为求测人与事体</span>
      </div>
    </div>
  );
}

export default function QimenAnalysis({ chart, category = 'general', question = '', focus = null }) {
  const rd = useMemo(() => crossReading(chart, category), [chart, category]);
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState('axis');
  if (!rd) return null;

  const shown = tab === 'self' ? rd.selfPalace
    : tab === 'matter' ? rd.matterPalace
      : tab === 'picked' && focus ? palaceReading(chart, focus) : null;

  const keying = [];
  const src = tab === 'axis' ? [rd.selfPalace, rd.matterPalace].filter(Boolean) : shown ? [shown] : [];
  const seen = new Set();
  src.forEach((p) => (p.keying || []).forEach((k) => {
    const key = p.num + k.pair;
    if (seen.has(key)) return;
    seen.add(key);
    keying.push({ ...k, num: p.num });
  }));

  return (
    <section className={'qm-drawer' + (open ? ' qm-drawer--open' : '')}>
      <button type="button" className="qm-drawer-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="qm-drawer-icon" style={{ background: TONE[rd.toneColor] || '#8E8E93' }}>盘面关系</span>
        <span className="qm-drawer-text">
          <span className="qm-drawer-title">
            就所问之事的关系判读
            <span className="qm-drawer-short">{rd.categoryLabel} · {chart.yinYang}遁{chart.ju}局</span>
          </span>
          <span className="qm-drawer-sum">
            {question ? `「${question}」　` : ''}
            {rd.axis ? `宫${rd.axis.self.num} → 宫${rd.axis.matter.num}：${rd.axis.rel}（${rd.axis.tone}）` : '用神未上盘'}
            <span className="qm-drawer-sum-tone" style={{ color: TONE[rd.toneColor] }}> · {rd.tone}</span>
          </span>
        </span>
        <span className="qm-drawer-tone" style={{ color: TONE[rd.toneColor] }}>{rd.tone}</span>
        <span className="qm-drawer-chev">{open ? '收起 ▾' : '展开 ▸'}</span>
      </button>

      {open && (
        <div className="qm-drawer-body">
          <RelationDiagram chart={chart} rd={rd} />

          <div className="qm-seg">
            <button type="button" className={'qm-seg-btn' + (tab === 'axis' ? ' qm-seg-btn--on' : '')} onClick={() => setTab('axis')}>主轴对照<span>人·事两宫</span></button>
            <button type="button" className={'qm-seg-btn' + (tab === 'self' ? ' qm-seg-btn--on' : '')} onClick={() => setTab('self')}>求测人宫<span>{rd.selfPalace ? '宫' + rd.selfPalace.num : '—'}</span></button>
            <button type="button" className={'qm-seg-btn' + (tab === 'matter' ? ' qm-seg-btn--on' : '')} onClick={() => setTab('matter')}>事体宫<span>{rd.matterPalace ? '宫' + rd.matterPalace.num : '—'}</span></button>
            <button type="button" className={'qm-seg-btn' + (tab === 'picked' ? ' qm-seg-btn--on' : '')} onClick={() => setTab('picked')} disabled={!focus}>所选宫<span>{focus ? '宫' + focus : '点盘面'}</span></button>
          </div>

          {tab === 'axis' ? (
            <>
              <div className="qm-axis">
                <Pillar title="求测人（日干）" p={rd.selfPalace} roleLabel="体" />
                <div className="qm-axis-rel">
                  <span className="qm-axis-arrow" style={{ color: TONE[rd.axis?.tone] }}>
                    {rd.axis?.actor === 'b' ? '←' : rd.axis?.actor === 'a' ? '→' : '＝'}
                  </span>
                  <span className="qm-axis-label" style={{ color: TONE[rd.axis?.tone] }}>{rd.axis?.rel}</span>
                  <span className="qm-axis-tone" style={{ color: TONE[rd.axis?.tone] }}>{rd.axis?.tone}</span>
                </div>
                <Pillar title="所测之事（时干）" p={rd.matterPalace} roleLabel="用" />
              </div>
              <p className="qm-hint">{rd.axis?.text}</p>
            </>
          ) : (
            <>
              <div className="qm-axis qm-axis--single">
                <Pillar title={tab === 'self' ? '求测人宫' : tab === 'matter' ? '事体宫' : `所选宫 ${focus}`} p={shown} roleLabel={tab === 'self' ? '体' : tab === 'matter' ? '用' : '选'} />
              </div>
              {shown && <p className="qm-hint">单宫读象：{shown.text}　｜　{shown.items.map((i) => `${i.kind}${i.name}`).join(' + ')}</p>}
            </>
          )}

          <div className="qm-rows">
            <div className="qm-row">
              <div className="qm-row-key"><b>用神定位</b><span>日干为身、时干为事，按所问之事增补</span></div>
              <div className="qm-row-vals">
                {rd.yong.map((y, i) => (
                  <span key={i} className={'qm-chip' + (y.palaces.length ? '' : ' qm-chip--muted')} title={y.note + '｜' + (y.plain || '')}>
                    {y.role} · {y.symbol}{y.palaces.length ? ` → 宫${y.palaces.join('/')}${y.vias?.length ? '（' + y.vias.join('/') + '）' : ''}` : ' 未上盘'}
                  </span>
                ))}
              </div>
            </div>

            <div className="qm-row">
              <div className="qm-row-key"><b>关键宫</b><span>值符值使、马星与用神落宫</span></div>
              <div className="qm-row-vals">
                {rd.keyPalaces.map((k, i) => (
                  <span key={i} className="qm-chip qm-chip--key">宫{k.num}（{GONG_BY_NUM[k.num]?.gua}）· {k.reason}</span>
                ))}
              </div>
            </div>

            {keying.length > 0 && (
              <div className="qm-row">
                <div className="qm-row-key"><b>十干克应</b><span>天盘干加地盘干（盘面实有）</span></div>
                <div className="qm-row-vals qm-row-vals--stack">
                  {keying.map((k, i) => (
                    <span key={i} className="qm-chip qm-chip--k" style={{ borderColor: TONE[k.tone] }} title={k.text}>
                      宫{k.num} {k.pair} · {k.name}<i style={{ color: TONE[k.tone] }}>{k.tone}</i>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="qm-row">
              <div className="qm-row-key"><b>四害</b><span>空亡 · 门迫 · 击刑 · 入墓</span></div>
              <div className="qm-row-vals qm-row-vals--stack">
                {rd.harms.length === 0 && <span className="qm-chip qm-chip--muted">盘面无四害标记</span>}
                {rd.harms.slice(0, 8).map((h, i) => (
                  <span key={i} className="qm-chip qm-chip--harm" title={h.detail}>宫{h.num}（{h.gua}）{h.type}·{h.label}</span>
                ))}
                {rd.harms.length > 8 && <span className="qm-chip qm-chip--muted">…共 {rd.harms.length} 项</span>}
              </div>
            </div>

            <div className="qm-row">
              <div className="qm-row-key"><b>应期线索</b><span>只作方向，不锁死日期</span></div>
              <div className="qm-row-vals qm-row-vals--stack">
                {rd.timing.length === 0 && <span className="qm-chip qm-chip--muted">此盘应期线索不足，断不出时间</span>}
                {rd.timing.slice(0, 4).map((t, i) => <span key={i} className="qm-chip qm-chip--time">{t}</span>)}
              </div>
            </div>
          </div>

          <div className="qm-advice" style={{ borderColor: TONE[rd.toneColor] }}>
            <span className="qm-advice-k" style={{ background: TONE[rd.toneColor] }}>断</span>
            <span className="qm-advice-v">
              整体倾向：{rd.tone}。
              {rd.axis?.tone === '吉' ? '人宫与事宫相生相得，宜顺势推进。' : rd.axis?.tone === '凶' ? '人宫受事宫所制，宜守势、先解压力再图进。' : '两宫比和或同宫，宜稳中求进、借力而行。'}
              {rd.harms.some((h) => h.type === '空亡') ? '盘中有空亡，事多虚而不实，待填实之期方见分晓。' : ''}
              {rd.harms.some((h) => h.type === '门迫') ? '有门迫之象，所行之路受阻，宜换门换法。' : ''}
            </span>
          </div>

          <div className="qm-legend">
            <span>阴盘取象：神＝外因与人事背景，门＝途径与人事，星＝天时与态势，干＝事之本质；宫位为体，组合读象。</span>
            <span>宫位生克以两宫五行论：生我者得助、我生者付出、克我者受阻、我克者可控、比和者相帮。</span>
          </div>
        </div>
      )}
    </section>
  );
}
